import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axios';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { LoadingPage } from '../components/feedback/Spinner';
import Layout from '../components/layout/Layout';

interface Project {
  id: number;
  address: string;
  status: string;
  credits_used: number;
  roof_area: number;
  siding_area: number;
  confidence_score: number;
  created_at: string;
}

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState<number | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get('/api/dashboard');
      setProjects(response.data.projects || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateReport = async (projectId: number) => {
    setIsGenerating(projectId);
    try {
      // Check if user has credits (for database projects)
      if (!user || user.credits <= 0) {
        alert('Insufficient credits. Please purchase more credits to generate reports.');
        return;
      }
      
      const response = await axios.post(`/api/generate-report/${projectId}`);
      if (response.data.report_path) {
        const downloadResponse = await axios.get(`/api/download-report/${projectId}`, {
          responseType: 'blob',
        });
        
        const blob = new Blob([downloadResponse.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `mavscope_report_${projectId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to generate report:', error);
      alert('Failed to generate report. Please try again.');
    } finally {
      setIsGenerating(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <Badge variant="success">Completed</Badge>;
      case 'processing':
        return <Badge variant="primary">Processing</Badge>;
      case 'pending':
        return <Badge variant="warning">Pending</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getUserInitials = () => {
    return user?.email?.charAt(0).toUpperCase() || 'U';
  };

  if (isLoading) {
    return <LoadingPage message="Loading dashboard..." />;
  }

  return (
    <Layout>
      <div className="dashboard-page">
        <header className="dashboard-header">
          <div className="dashboard-header-content">
            <div>
              <h1 className="dashboard-title">Dashboard</h1>
              <p className="dashboard-subtitle">Manage your property measurements and reports</p>
            </div>
            
            <Button 
              variant="primary"
              onClick={() => navigate('/analysis')}
              disabled={!user?.credits || user.credits < 1}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              New Analysis
            </Button>
          </div>
        </header>

        <main className="dashboard-content">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: 'var(--color-primary)' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-label">Total Projects</div>
                <div className="stat-value">{projects.length}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: 'var(--color-accent)' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-label">Credits Available</div>
                <div className="stat-value">{user?.credits || 0}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: 'var(--color-secondary)' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H17" />
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-label">Credits Used</div>
                <div className="stat-value">
                  {projects.reduce((sum, p) => sum + p.credits_used, 0)}
                </div>
              </div>
            </div>
          </div>

          <div className="projects-section">
            <h2 className="section-title">Recent Projects</h2>
              
              {projects.length === 0 ? (
                <Card>
                  <CardBody>
                    <div className="empty-state">
                      <div className="empty-state-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                          <polyline points="9 22 9 12 15 12 15 22" />
                        </svg>
                      </div>
                      <h3 className="empty-state-title">No projects yet</h3>
                      <p className="empty-state-description">
                        Start analyzing properties to see your projects here. Each analysis provides detailed roof and siding measurements.
                      </p>
                      <Button 
                        variant="primary"
                        onClick={() => navigate('/analysis')}
                        disabled={!user?.credits || user.credits < 1}
                      >
                        Start First Analysis
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              ) : (
                <div className="cards-grid">
                  {projects.map((project) => (
                    <Card key={project.id}>
                      <CardBody>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--spacing-4)' }}>
                          <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--color-gray-900)', flex: 1, marginRight: 'var(--spacing-3)' }}>
                            {project.address}
                          </h3>
                          {getStatusBadge(project.status)}
                        </div>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-5)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-sm)' }}>
                            <span style={{ color: 'var(--color-gray-500)' }}>Roof Area</span>
                            <span style={{ color: 'var(--color-gray-900)', fontWeight: 'var(--font-medium)' }}>
                              {project.roof_area?.toLocaleString()} sq ft
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-sm)' }}>
                            <span style={{ color: 'var(--color-gray-500)' }}>Siding Area</span>
                            <span style={{ color: 'var(--color-gray-900)', fontWeight: 'var(--font-medium)' }}>
                              {project.siding_area?.toLocaleString()} sq ft
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-sm)' }}>
                            <span style={{ color: 'var(--color-gray-500)' }}>Confidence</span>
                            <span style={{ color: 'var(--color-gray-900)', fontWeight: 'var(--font-medium)' }}>
                              {(project.confidence_score * 100).toFixed(1)}%
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-sm)' }}>
                            <span style={{ color: 'var(--color-gray-500)' }}>Date</span>
                            <span style={{ color: 'var(--color-gray-900)', fontWeight: 'var(--font-medium)' }}>
                              {new Date(project.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/analysis?projectId=${project.id}`)}
                          >
                            View Details
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            isLoading={isGenerating === project.id}
                            onClick={() => handleGenerateReport(project.id)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="7 10 12 15 17 10" />
                              <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                            Report
                          </Button>
                        </div>
                      </CardBody>
                    </Card>
                  ))}
                </div>
              )}
          </div>
        </main>
      </div>
    </Layout>
  );
};

export default Dashboard;
